import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import BarelyKetoOnboarding from "./screens/Onboarding";
import BarelyKetoDashboard from "./screens/Dashboard";
import BarelyKetoFAQ from "./screens/FAQ";

const C = {
  bg:"#FAF6EF", white:"#FFFFFF", sage:"#6B9E78", sageDark:"#4A7A57",
  sagePale:"#EBF4EC", sageLight:"#C8DFC8", gold:"#C8973C", goldPale:"#FDF3DC",
  goldLight:"#E8C96A", ink:"#2A2A2A", mid:"#4A4A4A", soft:"#6A6A6A",
  border:"#E0D8CC", light:"#F0EAE0", coral:"#E07A5F", navy:"#2C5F8A",
};
const font = "Palatino Linotype, Book Antiqua, Palatino, Georgia, serif";

function SplashScreen({ onLogin, onSignup }) {
  return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(160deg, ${C.sageDark} 0%, #1A3A20 100%)`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 28px", textAlign:"center", fontFamily:font }}>
      <style>{`@keyframes floatIn{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}} @keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}`}</style>
      <img src="/logo.png" alt="BarelyKeto" style={{ width:180, height:180, marginBottom:24, animation:"pulse 3s ease infinite", objectFit:"contain" }} />
      <h1 style={{ fontSize:38, color:C.white, fontFamily:font, fontWeight:"normal", marginBottom:10, lineHeight:1.2, animation:"floatIn 0.6s ease 0.2s both" }}>BarelyKeto</h1>
      <p style={{ fontSize:20, color:C.goldLight, fontFamily:font, fontStyle:"italic", marginBottom:12, animation:"floatIn 0.6s ease 0.35s both" }}>Keto Made Ridiculously Simple.</p>
      <p style={{ fontSize:16, color:"rgba(255,255,255,0.7)", fontFamily:font, lineHeight:1.7, maxWidth:300, marginBottom:52, animation:"floatIn 0.6s ease 0.5s both" }}>Your personal keto meal plan, done for you every week. No thinking required.</p>
      <div style={{ width:"100%", maxWidth:360, display:"flex", flexDirection:"column", gap:14, animation:"floatIn 0.6s ease 0.65s both" }}>
        <button onClick={onSignup} style={{ padding:"20px", background:`linear-gradient(135deg, ${C.goldLight}, ${C.gold})`, color:C.sageDark, border:"none", borderRadius:16, fontSize:19, fontFamily:font, fontWeight:"bold", cursor:"pointer", boxShadow:`0 6px 20px rgba(0,0,0,0.3)`, letterSpacing:0.3 }}>
          Start My Free Trial →
        </button>
        <button onClick={onLogin} style={{ padding:"18px", background:"rgba(255,255,255,0.12)", color:C.white, border:"1.5px solid rgba(255,255,255,0.3)", borderRadius:16, fontSize:17, fontFamily:font, cursor:"pointer" }}>
          I already have an account
        </button>
      </div>
      <p style={{ fontSize:13, color:"rgba(255,255,255,0.5)", fontFamily:font, marginTop:28 }}>3 days free · $19.99/month after · Cancel any time</p>
    </div>
  );
}

function CheckEmailScreen({ email }) {
  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 28px", textAlign:"center", fontFamily:font }}>
      <div style={{ fontSize:72, marginBottom:24 }}>📧</div>
      <h1 style={{ fontSize:28, color:C.ink, fontFamily:font, fontWeight:"normal", marginBottom:12, lineHeight:1.3 }}>Check your email</h1>
      <p style={{ fontSize:17, color:C.mid, fontFamily:font, lineHeight:1.7, maxWidth:320, marginBottom:24 }}>
        We've sent a confirmation link to <strong>{email}</strong>. Click the link in the email to continue setting up your account.
      </p>
      <div style={{ background:C.sagePale, border:`1px solid ${C.sageLight}`, borderRadius:14, padding:"16px 20px", maxWidth:320, marginBottom:24 }}>
        <p style={{ fontSize:15, color:C.sageDark, fontFamily:font, lineHeight:1.6 }}>After clicking the link you'll be brought back here to complete your personalised meal plan setup.</p>
      </div>
      <p style={{ fontSize:14, color:C.soft, fontFamily:font, lineHeight:1.6 }}>Can't find the email? Check your spam folder.</p>
    </div>
  );
}

function AuthScreen({ mode, onAuth, onBack }) {
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [agreed,   setAgreed]   = useState(false);
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [errors,   setErrors]   = useState({});

  const isSignup = mode === "signup";
  const validate = () => {
    const e = {};
    if (isSignup && !name.trim()) e.name = "Please enter your first name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Please enter a valid email address.";
    if (password.length < 8) e.password = "Password must be at least 8 characters.";
    if (isSignup && password !== confirm) e.confirm = "Passwords don't match.";
    if (isSignup && !agreed) e.agreed = "Please agree to continue.";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setLoading(true);
    try {
      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { name: name.trim() } }
        });
        if (error) { setErrors({ email: error.message }); setLoading(false); return; }
        if (data.user) {
          const referredBy = localStorage.getItem('bk_ref') || null;
          await fetch('/api/init-profile', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: data.user.id,
              email,
              name: name.trim(),
              ...(referredBy ? { referred_by: referredBy } : {}),
            }),
          });
          if (referredBy) localStorage.removeItem('bk_ref');
        }
        localStorage.setItem('bk_pending_name', name.trim());
        onAuth({ email, name: name.trim(), isNewUser: true });
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) { setErrors({ email: 'Incorrect email or password.' }); setLoading(false); return; }
        const { data: profileData } = await supabase.from('profiles').select('*').eq('id', data.user.id).single();
        onAuth({
          id: data.user.id,
          email,
          name: profileData?.name || data.user?.user_metadata?.name || email.split('@')[0],
          currentWeight: profileData?.current_weight ? String(profileData.current_weight) : null,
          goalWeight: profileData?.goal_weight ? String(profileData.goal_weight) : null,
          goal: profileData?.goal,
          health: profileData?.health,
          country: profileData?.country,
          supermarket: profileData?.supermarket,
          cookTime: profileData?.cook_time,
          cookSkill: profileData?.cook_skill,
          budget: profileData?.budget,
          exercise: profileData?.exercise,
          proteins: profileData?.proteins,
          allergies: profileData?.allergies,
          dislikes: profileData?.dislikes,
          units: profileData?.units,
          servings: profileData?.servings,
          timeframe: profileData?.timeframe,
          height: profileData?.height ? String(profileData.height) : null,
          snacks: profileData?.snacks,
          leftovers: profileData?.leftovers,
          experience: profileData?.experience,
          macros: profileData?.macros,
          control: profileData?.control,
          age: profileData?.age ? String(profileData.age) : null,
          referralCode: profileData?.referral_code || null,
          referralCount: profileData?.referral_count || 0,
          isNewUser: false,
        });
      }
    } catch(err) {
      setErrors({ email: 'Something went wrong. Please try again.' });
      setLoading(false);
    }
  };

  const pwStrength = (pw) => {
    if (!pw) return null;
    if (pw.length < 6)  return { label:"Too short",  color:C.coral,    w:"25%" };
    if (pw.length < 8)  return { label:"Weak",        color:"#E67E22",  w:"50%" };
    if (pw.length < 12) return { label:"Good",        color:C.gold,     w:"75%" };
    return               { label:"Strong",       color:C.sage,     w:"100%" };
  };
  const strength = pwStrength(password);

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column", fontFamily:font }}>
      <style>{`input:focus{outline:none!important;border-color:${C.sage}!important;box-shadow:0 0 0 3px ${C.sage}22!important} *{box-sizing:border-box} input[type=checkbox]{accent-color:${C.sage}}`}</style>
      <div style={{ padding:"20px 24px 0" }}>
        <button onClick={onBack} style={{ background:"none", border:"none", cursor:"pointer", color:C.mid, fontSize:22, padding:0 }}>←</button>
      </div>
      <div style={{ padding:"24px 28px 0", textAlign:"center", marginBottom:28 }}>
        <img src="/logo.png" alt="BarelyKeto" style={{ width:100, height:100, marginBottom:12, objectFit:"contain" }} />
        <h1 style={{ fontSize:28, color:C.ink, fontFamily:font, fontWeight:"normal", marginBottom:6 }}>{isSignup ? "Start your free trial" : "Welcome back"}</h1>
        <p style={{ fontSize:16, color:C.soft, fontFamily:font }}>{isSignup ? "3-day FREE trial · Cancel any time" : "Log in to your BarelyKeto plan"}</p>
      </div>
      {isSignup && (
        <div style={{ margin:"0 28px 20px", background:C.sagePale, borderRadius:14, padding:"14px 16px", border:`1px solid ${C.sageLight}` }}>
          {["Your personalised 3-day keto meal plan","Automatic shopping list","Daily keto tips"].map((item,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"4px 0" }}>
              <span style={{ color:C.sage, fontSize:15 }}>✓</span>
              <span style={{ fontSize:15, color:C.mid, fontFamily:font }}>{item}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ flex:1, padding:"0 28px" }}>
        {[
          ...(isSignup ? [{ label:"Your first name", type:"text", value:name, set:setName, err:errors.name, hint:null, placeholder:"e.g. Sarah" }] : []),
          { label:"Email address", type:"email", value:email, set:setEmail, err:errors.email, hint: isSignup ? "We'll send your meal plan and tips here." : null, placeholder:"your@email.com" },
          { label: isSignup ? "Create a password" : "Password", type: showPw ? "text" : "password", value:password, set:setPassword, err:errors.password, placeholder:"At least 8 characters", isPw:true },
          ...(isSignup ? [{ label:"Confirm password", type:showPw?"text":"password", value:confirm, set:setConfirm, err:errors.confirm, placeholder:"Type your password again" }] : []),
        ].map((field, i) => (
          <div key={i} style={{ marginBottom:18 }}>
            <label style={{ fontSize:15, color:C.mid, fontFamily:font, display:"block", marginBottom:8, fontWeight:"bold" }}>{field.label}</label>
            <div style={{ position:"relative" }}>
              <input type={field.type} value={field.value} onChange={e => field.set(e.target.value)} placeholder={field.placeholder}
                style={{ width:"100%", padding:"16px 20px", paddingRight: field.isPw ? 52 : 20, fontSize:18, border:`2px solid ${field.err ? C.coral : C.border}`, borderRadius:14, background:C.white, color:C.ink, fontFamily:font }} />
              {field.isPw && (
                <button onClick={() => setShowPw(s=>!s)} style={{ position:"absolute", right:16, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:18, color:C.soft }}>
                  {showPw ? "🙈" : "👁️"}
                </button>
              )}
            </div>
            {field.err && <p style={{ fontSize:14, color:C.coral, fontFamily:font, marginTop:6 }}>{field.err}</p>}
            {field.hint && !field.err && <p style={{ fontSize:14, color:C.soft, fontFamily:font, marginTop:6 }}>{field.hint}</p>}
            {field.isPw && isSignup && strength && (
              <div style={{ marginTop:8 }}>
                <div style={{ height:4, background:C.border, borderRadius:2, overflow:"hidden", marginBottom:4 }}>
                  <div style={{ height:"100%", width:strength.w, background:strength.color, transition:"all 0.3s", borderRadius:2 }} />
                </div>
                <p style={{ fontSize:13, color:strength.color, fontFamily:font }}>{strength.label}</p>
              </div>
            )}
          </div>
        ))}
        {!isSignup && (
          <div style={{ textAlign:"right", marginTop:-10, marginBottom:20 }}>
            <button onClick={async () => {
              if (!email) { setErrors({ email: "Enter your email address first." }); return; }
              const { error } = await supabase.auth.resetPasswordForEmail(email);
              if (error) { setErrors({ email: error.message }); }
              else { setErrors({ email: "✅ Password reset email sent — check your inbox." }); }
            }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:15, color:C.sage, fontFamily:font }}>Forgot password?</button>
          </div>
        )}
        {isSignup && (
          <div style={{ marginBottom: errors.agreed ? 8 : 20 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <input type="checkbox" checked={agreed} onChange={e=>setAgreed(e.target.checked)} style={{ width:20, height:20, marginTop:2, flexShrink:0, cursor:"pointer" }} />
              <p style={{ fontSize:15, color:C.mid, fontFamily:font, lineHeight:1.6 }}>
                I agree to the <span style={{ color:C.sage }}>Terms of Service</span> and <span style={{ color:C.sage }}>Privacy Policy</span>. BarelyKeto provides general dietary guidance and is not a substitute for medical advice.
              </p>
            </div>
            {errors.agreed && <p style={{ fontSize:14, color:C.coral, fontFamily:font, marginTop:8 }}>{errors.agreed}</p>}
          </div>
        )}
        <button onClick={handleSubmit} disabled={loading} style={{ width:"100%", padding:"20px", background:loading?C.border:`linear-gradient(135deg, ${C.sage}, ${C.sageDark})`, color:C.white, border:"none", borderRadius:16, fontSize:18, fontFamily:font, fontWeight:"bold", cursor:loading?"not-allowed":"pointer", boxShadow:loading?"none":`0 6px 20px ${C.sage}44`, marginBottom:20, letterSpacing:0.3 }}>
          {loading ? (isSignup ? "Creating account..." : "Logging in...") : (isSignup ? "Create My Account →" : "Log In →")}
        </button>
      </div>
    </div>
  );
}

function GeneratingScreen({ userName, profile, onComplete, isTrial, mealRatings }) {
  const [progress, setProgress] = useState(0);
  const messages = [
    "Reading your food preferences...",
    "Checking your health profile...",
    "Calculating your calorie targets for Day 1...",
    "Creating your Day 1 recipes...",
    "Creating your Day 2 recipes...",
    "Creating your Day 3 recipes...",
    "Making sure no meals are repeated...",
    "Almost ready...",
  ];
  const msgIdx = Math.min(Math.floor(progress / 12), messages.length - 1);

  useEffect(() => {
    let cancelled = false;
    const ticker = setInterval(() => setProgress(p => p < 88 ? p + 0.4 : p), 400);

    const generateAll = async () => {
      try {
        // Use prop profile, fall back to localStorage if React state hasn't updated yet
        let activeProfile = profile;
        if (!activeProfile || !activeProfile.country) {
          const saved = localStorage.getItem('bk_profile');
          if (saved) { try { activeProfile = JSON.parse(saved); } catch(e) {} }
        }

        const dayNames = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
        const totalDays = isTrial ? 3 : 7;
        const allDays = [];
        const usedMeals = [];

        for (let i = 0; i < totalDays; i++) {
          if (cancelled) return;
          setProgress(Math.round((i / totalDays) * 90));
          const likedMeals = Object.entries(mealRatings || {}).filter(([,v]) => v === 'liked').map(([k]) => k);
          const dislikedMeals = Object.entries(mealRatings || {}).filter(([,v]) => v === 'disliked').map(([k]) => k);
          const r = await fetch('/api/generate-plan', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              profile: activeProfile,
              dayNumber: i + 1,
              dayName: dayNames[i],
              previousMeals: usedMeals,
              likedMeals,
              dislikedMeals,
            }),
          });
          const day = await r.json();
          if (day && day.meals) {
            allDays.push(day);
            Object.values(day.meals).forEach(m => m?.name && usedMeals.push(m.name));
          }
        }

        clearInterval(ticker);
        if (!cancelled) {
          setProgress(100);
          setTimeout(() => onComplete(allDays.length > 0 ? allDays : null), 600);
        }
      } catch(e) {
        clearInterval(ticker);
        if (!cancelled) { setProgress(100); setTimeout(() => onComplete(null), 600); }
      }
    };

    generateAll();
    return () => { cancelled = true; clearInterval(ticker); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 32px", textAlign:"center", fontFamily:font }}>
      <style>{`@keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}`}</style>
      <img src="/logo.png" alt="BarelyKeto" style={{ width:140, height:140, marginBottom:24, animation:"pulse 1.8s ease infinite", objectFit:"contain" }} />
      <h2 style={{ fontSize:26, color:C.ink, fontFamily:font, fontWeight:"normal", marginBottom:10, lineHeight:1.35 }}>Building your plan, {userName}...</h2>
      <p style={{ fontSize:17, color:C.sage, fontFamily:font, fontStyle:"italic", marginBottom:36, minHeight:26 }}>{messages[msgIdx]}</p>
      <div style={{ width:"100%", maxWidth:320, height:8, background:C.border, borderRadius:4, overflow:"hidden", marginBottom:14 }}>
        <div style={{ height:"100%", width:`${progress}%`, background:`linear-gradient(90deg, ${C.sageLight}, ${C.sage})`, borderRadius:4, transition:"width 0.3s ease" }} />
      </div>
      <p style={{ fontSize:14, color:C.soft, fontFamily:font }}>{Math.round(progress)}% complete</p>
      <div style={{ marginTop:24, background:C.goldPale, border:`1px solid ${C.goldLight}`, borderRadius:12, padding:"12px 20px", maxWidth:320 }}>
        <p style={{ fontSize:14, color:C.gold, fontFamily:font, fontWeight:"bold", textAlign:"center" }}>⚠️ Please don't close this screen</p>
        <p style={{ fontSize:13, color:C.mid, fontFamily:font, textAlign:"center", marginTop:4, lineHeight:1.6 }}>We're building your personalised recipes one by one. This takes 2-3 minutes. Closing this page will stop the process.</p>
      </div>
    </div>
  );
}

function PricingScreen({ onBack, user }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [plan,        setPlan]        = useState("meal");
  const [paying,      setPaying]      = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const price     = plan === "bundle" ? "24.99" : "19.99";
  const planLabel = plan === "bundle" ? "Meal + Exercise Plan" : "Meal Plan";

  const FEATURES = [
    { emoji:"🧬", text:"Built around you — your goals, health and food preferences. No two plans are ever the same." },
    { emoji:"📅", text:"Fresh weekly meal plan, AI-generated and fully personalised" },
    { emoji:"🛒", text:"Automatic shopping list updated every week" },
    { emoji:"🔄", text:"5 meal swaps per week" },
    { emoji:"📊", text:"Weekly weight tracker with progress chart" },
    { emoji:"💡", text:"Daily keto tips" },
    { emoji:"🛍️", text:"Shop online direct from your list" },
    { emoji:"✏️", text:"Update preferences any time — your next plan adjusts automatically" },
  ];

  return (
    <div style={{ maxWidth:480, margin:"0 auto", minHeight:"100vh", background:C.bg, fontFamily:font, overflowY:"auto" }}>
      <style>{`* { box-sizing:border-box; } ::-webkit-scrollbar { width:0; }`}</style>

      {showConfirm && (
        <div style={{ position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", padding:28 }}>
          <div style={{ background:C.white, borderRadius:24, padding:"32px 28px", maxWidth:340, width:"100%" }}>
            <p style={{ fontSize:36, textAlign:"center", marginBottom:14 }}>✅</p>
            <h3 style={{ fontSize:21, color:C.ink, fontFamily:font, fontWeight:"normal", marginBottom:14, textAlign:"center" }}>Confirm your subscription</h3>
            <div style={{ background:C.sagePale, borderRadius:14, padding:"16px", marginBottom:18 }}>
              {[["Plan", planLabel],["Trial period","3 days free"],["After trial",`$${price}/month`]].map(([l,v],i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:i<2?`1px solid ${C.sageLight}`:"none" }}>
                  <span style={{ fontSize:16, color:C.mid, fontFamily:font }}>{l}</span>
                  <span style={{ fontSize:16, color:C.sageDark, fontFamily:font, fontWeight:"bold" }}>{v}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize:14, color:C.soft, fontFamily:font, lineHeight:1.7, marginBottom:20, textAlign:"center" }}>
              You won't be charged during your 3-day trial. ${price}/month charged automatically after. Cancel any time before trial ends at no cost.
            </p>
            <button onClick={async () => {
                setShowConfirm(false); setPaying(true);
                try {
                  const priceId = plan === 'bundle'
                    ? process.env.REACT_APP_STRIPE_PRICE_BUNDLE
                    : process.env.REACT_APP_STRIPE_PRICE_MEAL;
                  const res = await fetch('/api/create-checkout', {
                    method:'POST',
                    headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({ priceId, email: user?.email || '', plan }),
                  });
                  const data = await res.json();
                  if (data.url) { window.location.href = data.url; }
                  else { alert('Checkout error: ' + (data.error || 'Unknown error. Please try again.')); setPaying(false); }
                } catch(e) { alert('Something went wrong. Please try again.'); setPaying(false); }
              }}
              style={{ width:"100%", padding:"16px", background:`linear-gradient(135deg, ${C.sage}, ${C.sageDark})`, color:C.white, border:"none", borderRadius:14, fontSize:17, fontFamily:font, fontWeight:"bold", cursor:"pointer", marginBottom:12 }}>
              Start My Free Trial →
            </button>
            <button onClick={() => setShowConfirm(false)} style={{ width:"100%", padding:"14px", background:"none", border:`1.5px solid ${C.border}`, borderRadius:14, fontSize:15, color:C.mid, fontFamily:font, cursor:"pointer" }}>
              Go back
            </button>
          </div>
        </div>
      )}

      {onBack && <div style={{ padding:"20px 24px 0" }}><button onClick={onBack} style={{ background:"none", border:"none", cursor:"pointer", color:C.mid, fontSize:22, padding:0 }}>←</button></div>}

      <div style={{ padding:"32px 28px 24px", textAlign:"center" }}>
        <img src="/logo.png" alt="BarelyKeto" style={{ width:100, height:100, marginBottom:14, objectFit:"contain" }} />
        <h1 style={{ fontSize:28, color:C.ink, fontFamily:font, fontWeight:"normal", marginBottom:10, lineHeight:1.3 }}>Keto Made Ridiculously Simple.</h1>
        <p style={{ fontSize:17, color:C.soft, fontFamily:font, lineHeight:1.7 }}>Your personal keto plan, done for you every week.</p>
      </div>


      <div style={{ margin:"0 24px 20px" }}>
        <div style={{ background:C.sagePale, border:`2px solid ${C.sage}`, borderRadius:14, padding:"12px 16px", marginBottom:16, textAlign:"center" }}>
          <p style={{ fontSize:17, color:C.sageDark, fontFamily:font, fontWeight:"bold", marginBottom:4 }}>👇 Tap a plan to select it</p>
          <p style={{ fontSize:14, color:C.mid, fontFamily:font }}>Your 3-day free trial starts immediately. No charge today.</p>
        </div>
        <button onClick={() => setPlan("meal")}
          style={{ width:"100%", padding:"18px 20px", marginBottom:16, background:plan==="meal"?`linear-gradient(135deg, ${C.sage}, ${C.sageDark})`:C.white, border:plan==="meal"?`3px solid ${C.sage}`:`2px solid ${C.border}`, borderRadius:16, display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", transition:"all 0.2s", boxShadow:plan==="meal"?`0 4px 16px ${C.sage}44`:`0 2px 8px rgba(0,0,0,0.06)` }}>
          <div style={{ textAlign:"left" }}>
            <p style={{ fontSize:18, color:plan==="meal"?C.white:C.ink, fontFamily:font, fontWeight:"bold", marginBottom:3 }}>🥑 Meal Plan</p>
            <p style={{ fontSize:14, color:plan==="meal"?"rgba(255,255,255,0.85)":C.soft, fontFamily:font }}>Personalised weekly meal plan</p>
          </div>
          <div style={{ textAlign:"right" }}>
            <p style={{ fontSize:24, color:plan==="meal"?C.white:C.ink, fontFamily:font, fontWeight:"bold", lineHeight:1 }}>$19.99</p>
            <p style={{ fontSize:13, color:plan==="meal"?"rgba(255,255,255,0.8)":C.soft, fontFamily:font }}>per month</p>
          </div>
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
          <div style={{ flex:1, height:1, background:C.border }} />
          <p style={{ fontSize:13, color:C.soft, fontFamily:font, fontWeight:"bold" }}>OR</p>
          <div style={{ flex:1, height:1, background:C.border }} />
        </div>
        <div style={{ position:"relative" }}>
          <div style={{ position:"absolute", top:-12, left:"50%", transform:"translateX(-50%)", zIndex:2, background:`linear-gradient(135deg, ${C.gold}, #A8742A)`, color:C.white, fontSize:13, fontFamily:font, fontWeight:"bold", borderRadius:20, padding:"4px 16px", whiteSpace:"nowrap", boxShadow:`0 2px 8px ${C.gold}66` }}>
            ⭐ Most Popular — Best Value
          </div>
          <button onClick={() => setPlan("bundle")}
            style={{ width:"100%", padding:"20px", background:plan==="bundle"?`linear-gradient(135deg, ${C.navy}, #0F2440)`:C.white, border:plan==="bundle"?`3px solid ${C.navy}`:`3px solid ${C.navy}`, borderRadius:16, display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", transition:"all 0.2s", boxShadow:plan==="bundle"?`0 4px 20px rgba(44,95,138,0.5)`:`0 2px 12px rgba(44,95,138,0.2)` }}>
            <div style={{ textAlign:"left" }}>
              <p style={{ fontSize:18, color:plan==="bundle"?C.white:C.navy, fontFamily:font, fontWeight:"bold", marginBottom:3 }}>🏃 Meal Plan + Exercise</p>
              <p style={{ fontSize:14, color:plan==="bundle"?"rgba(255,255,255,0.85)":C.mid, fontFamily:font, marginBottom:6 }}>Meals AND a personalised exercise plan</p>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {["Faster results","More accountability","Reviewed monthly"].map((tag,i) => (
                  <span key={i} style={{ fontSize:12, color:plan==="bundle"?"rgba(255,255,255,0.9)":C.navy, background:plan==="bundle"?"rgba(255,255,255,0.15)":"rgba(44,95,138,0.1)", borderRadius:20, padding:"2px 10px", fontFamily:font }}>✓ {tag}</span>
                ))}
              </div>
            </div>
            <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
              <p style={{ fontSize:28, color:plan==="bundle"?C.white:C.navy, fontFamily:font, fontWeight:"bold", lineHeight:1 }}>$24.99</p>
              <p style={{ fontSize:12, color:plan==="bundle"?"rgba(255,255,255,0.8)":C.soft, fontFamily:font }}>per month</p>
              <p style={{ fontSize:12, color:plan==="bundle"?C.goldLight:C.gold, fontFamily:font, fontWeight:"bold", marginTop:4 }}>Only $5 more!</p>
            </div>
          </button>
        </div>
      </div>

      <div style={{ margin:"0 24px 24px", background:plan==="bundle"?`linear-gradient(160deg, #1A3A5C, #0F2440)`:`linear-gradient(160deg, ${C.sageDark}, #2A5A35)`, borderRadius:24, padding:"28px 24px", textAlign:"center" }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"center", gap:4, marginBottom:8 }}>
          <span style={{ fontSize:20, color:C.white, fontFamily:font, paddingTop:10 }}>$</span>
          <span style={{ fontSize:68, color:C.white, fontFamily:font, fontWeight:"bold", lineHeight:1 }}>{plan==="bundle"?"24":"19"}</span>
          <span style={{ fontSize:20, color:C.white, fontFamily:font, paddingTop:10 }}>.99</span>
        </div>
        <p style={{ fontSize:15, color:"rgba(255,255,255,0.7)", fontFamily:font, marginBottom:20 }}>AUD per month</p>
        <div style={{ display:"flex", justifyContent:"center", gap:8, flexWrap:"wrap", marginBottom:24 }}>
          {["✓ 3-day free trial","✓ Cancel any time","✓ No lock-in"].map((t,i) => (
            <span key={i} style={{ fontSize:13, color:C.white, fontFamily:font, background:"rgba(255,255,255,0.15)", borderRadius:20, padding:"5px 12px" }}>{t}</span>
          ))}
        </div>
        <button onClick={() => setShowConfirm(true)} disabled={paying} style={{ width:"100%", padding:"18px", background:paying?"rgba(255,255,255,0.3)":`linear-gradient(135deg, ${C.goldLight}, ${C.gold})`, color:paying?C.white:C.sageDark, border:"none", borderRadius:16, fontSize:18, fontFamily:font, fontWeight:"bold", cursor:paying?"not-allowed":"pointer", letterSpacing:0.3 }}>
          {paying ? "Setting up your account..." : "Start My Free Trial →"}
        </button>
        <p style={{ fontSize:12, color:"rgba(255,255,255,0.6)", fontFamily:font, marginTop:12, lineHeight:1.6 }}>
          Your card will not be charged for 3 days. ${price}/month after. Cancel any time before day 3.
        </p>
        <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:14, flexWrap:"wrap" }}>
          {["💳 Visa & Mastercard","Apple Pay","PayPal","Google Pay"].map((m,i) => (
            <span key={i} style={{ fontSize:11, color:"rgba(255,255,255,0.65)", fontFamily:font, background:"rgba(255,255,255,0.1)", borderRadius:8, padding:"3px 8px" }}>{m}</span>
          ))}
        </div>
      </div>

      <div style={{ padding:"0 24px 28px" }}>
        <p style={{ fontSize:13, color:C.soft, fontFamily:font, fontWeight:"bold", textTransform:"uppercase", letterSpacing:1.5, marginBottom:16 }}>Everything included</p>
        <div style={{ background:C.white, borderRadius:16, padding:"18px 20px 4px", border:`1px solid ${C.border}` }}>
          {FEATURES.map((f,i) => (
            <div key={i} style={{ display:"flex", gap:14, paddingBottom:14 }}>
              <div style={{ width:34, height:34, borderRadius:10, background:C.sagePale, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{f.emoji}</div>
              <p style={{ fontSize:16, color:C.mid, fontFamily:font, lineHeight:1.55, paddingTop:5 }}>{f.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding:"0 24px 52px" }}>
        <div style={{ textAlign:"center", marginBottom:16 }}>
          <p style={{ fontSize:20, color:C.ink, fontFamily:font, fontWeight:"bold", marginBottom:6 }}>👇 Tap to choose your plan</p>
          <p style={{ fontSize:14, color:C.soft, fontFamily:font }}>Select one to start your free 3-day trial</p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {[
            { id:"meal", label:"Meal Plan only", sub:"3 days free, then $19.99/month", c1:C.sage, c2:C.sageDark },
            { id:"bundle", label:"Meal Plan + Exercise", sub:"3 days free, then $24.99/month", c1:C.navy, c2:"#1A3A5C", badge:"Popular" },
          ].map(p => (
            <button key={p.id} onClick={() => { setPlan(p.id); setShowConfirm(true); }}
              style={{ padding:"18px 20px", background:`linear-gradient(135deg, ${p.c1}, ${p.c2})`, color:C.white, border:"none", borderRadius:16, fontSize:16, fontFamily:font, fontWeight:"bold", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", position:"relative" }}>
              <div style={{ textAlign:"left" }}>
                <p style={{ fontSize:17, marginBottom:2 }}>{p.label}</p>
                <p style={{ fontSize:13, opacity:0.85, fontWeight:"normal" }}>{p.sub}</p>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                {p.badge && <span style={{ fontSize:11, background:C.gold, color:C.white, borderRadius:20, padding:"2px 8px", fontWeight:"bold" }}>{p.badge}</span>}
                <span style={{ fontSize:18 }}>→</span>
              </div>
            </button>
          ))}
        </div>
        <p style={{ fontSize:13, color:C.soft, fontFamily:font, textAlign:"center", marginTop:16 }}>Cancel any time before day 3 — no charge at all</p>
      </div>
    </div>
  );
}

export default function BarelyKetoApp() {
  const [screen,        setScreen]        = useState("splash");
  const [user,          setUser]          = useState(null);
  const [isTrialUser,   setIsTrialUser]   = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [onboardingAnswers, setOnboardingAnswers] = useState(null);
  const [selectedPlan,  setSelectedPlan]  = useState("meal");
  const [isEditingPreferences, setIsEditingPreferences] = useState(false);
  const [mealRatings,   setMealRatings]   = useState({});

  useEffect(() => {
    // Capture referral code from URL so it survives to signup
    const refParam = new URLSearchParams(window.location.search).get('ref');
    if (refParam) localStorage.setItem('bk_ref', refParam);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('checkout')) return;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      const savedWeight      = localStorage.getItem('bk_weight');
      const savedGoalWeight  = localStorage.getItem('bk_goalweight');
      const savedPlan        = localStorage.getItem('bk_plan');
      const pendingName      = localStorage.getItem('bk_pending_name');
      if (savedPlan) setSelectedPlan(savedPlan);
      const fullUser = {
        id: session.user.id,
        email: session.user.email,
        name: profile?.name || session.user.user_metadata?.name || pendingName || session.user.email.split('@')[0],
        currentWeight: profile?.current_weight ? String(profile.current_weight) : savedWeight || null,
        goalWeight: profile?.goal_weight ? String(profile.goal_weight) : savedGoalWeight || null,
        goal: profile?.goal,
        health: profile?.health,
        country: profile?.country,
        supermarket: profile?.supermarket,
        cookTime: profile?.cook_time,
        cookSkill: profile?.cook_skill,
        budget: profile?.budget,
        exercise: profile?.exercise,
        proteins: profile?.proteins,
        allergies: profile?.allergies,
        dislikes: profile?.dislikes,
        units: profile?.units,
        servings: profile?.servings,
        timeframe: profile?.timeframe,
        height: profile?.height ? String(profile.height) : null,
        snacks: profile?.snacks,
        leftovers: profile?.leftovers,
        experience: profile?.experience,
        macros: profile?.macros,
        control: profile?.control,
        age: profile?.age ? String(profile.age) : null,
        referralCode: profile?.referral_code || null,
        referralCount: profile?.referral_count || 0,
        subscriptionStatus: profile?.subscription_status || 'trial',
      };
      setUser(fullUser);
      setOnboardingAnswers(fullUser);
      setIsTrialUser(profile?.subscription_status !== 'active');

      // Capture referral code from URL on session restore
      const refParam = new URLSearchParams(window.location.search).get('ref');
      if (refParam) localStorage.setItem('bk_ref', refParam);

      // New user -- confirmed email but no onboarding yet
      if (!profile?.onboarding_complete) {
        setScreen('onboarding');
        return;
      }

      // Trial just converted to paid — generate a fresh plan immediately
      if (profile?.needs_new_plan) {
        await supabase.from('profiles').update({ needs_new_plan: false }).eq('id', session.user.id);
        localStorage.removeItem('bk_generated_plan');
        localStorage.removeItem('bk_plan_date');
        localStorage.removeItem('bk_paid_plan_date');
        setScreen('generating');
        return;
      }

      // Returning user -- load saved meal plan
      const { data: planData } = await supabase
        .from('meal_plans')
        .select('plan_data')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (planData?.plan_data) {
        try { setGeneratedPlan(planData.plan_data); } catch(e) {}
        setScreen('dashboard');
      } else {
        // No plan in Supabase — clear stale local cache and generate fresh
        localStorage.removeItem('bk_generated_plan');
        localStorage.removeItem('bk_plan_date');
        setScreen('generating');
      }
    };
    checkSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const plan = params.get("plan");
    if (checkout === "success") {
      window.history.replaceState({}, "", "/app");
      const resolvedPlan = plan || "meal";
      setSelectedPlan(resolvedPlan);
      localStorage.setItem('bk_plan', resolvedPlan);
      setIsTrialUser(true);

      // Load profile from Supabase (most reliable source after checkout redirect)
      const loadAndGenerate = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single();
            const fullUser = {
              id: session.user.id,
              email: session.user.email,
              name: profile?.name || session.user.user_metadata?.name || session.user.email.split('@')[0],
              currentWeight: profile?.current_weight ? String(profile.current_weight) : null,
              goalWeight: profile?.goal_weight ? String(profile.goal_weight) : null,
              goal: profile?.goal,
              health: profile?.health,
              country: profile?.country,
              supermarket: profile?.supermarket,
              cookTime: profile?.cook_time,
              cookSkill: profile?.cook_skill,
              budget: profile?.budget,
              exercise: profile?.exercise,
              proteins: profile?.proteins,
              allergies: profile?.allergies,
              dislikes: profile?.dislikes,
              units: profile?.units,
              servings: profile?.servings,
              timeframe: profile?.timeframe,
              height: profile?.height ? String(profile.height) : null,
              snacks: profile?.snacks,
              leftovers: profile?.leftovers,
              experience: profile?.experience,
              macros: profile?.macros,
              control: profile?.control,
              age: profile?.age ? String(profile.age) : null,
              referralCode: profile?.referral_code || null,
              referralCount: profile?.referral_count || 0,
            };
            setUser(fullUser);
            setOnboardingAnswers(fullUser);
            // Store in localStorage so GeneratingScreen has it immediately
            localStorage.setItem('bk_profile', JSON.stringify(fullUser));
          } else {
            // Fallback to localStorage if no session
            const savedProfile = localStorage.getItem("bk_profile");
            if (savedProfile) {
              try {
                const profile = JSON.parse(savedProfile);
                setUser(profile);
                setOnboardingAnswers(profile);
              } catch(e) {}
            }
          }
        } catch(e) {
          const savedProfile = localStorage.getItem("bk_profile");
          if (savedProfile) {
            try {
              const profile = JSON.parse(savedProfile);
              setUser(profile);
              setOnboardingAnswers(profile);
            } catch(e2) {}
          }
        }
        setScreen("generating");
      };
      loadAndGenerate();

    } else if (checkout === "cancelled") {
      window.history.replaceState({}, "", "/app");
      const savedProfile = localStorage.getItem("bk_profile");
      if (savedProfile) {
        try {
          const profile = JSON.parse(savedProfile);
          setUser(profile);
          setOnboardingAnswers(profile);
        } catch(e) {}
      }
      setScreen("pricing");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuth = async (userData) => {
    // Clear any stale localStorage from a previous account
    ['bk_profile','bk_generated_plan','bk_plan','bk_weight','bk_goalweight','bk_pending_name'].forEach(k => localStorage.removeItem(k));
    setUser(userData);
    setOnboardingAnswers(userData);
    if (userData.isNewUser) {
      setScreen("checkemail");
    } else {
      setIsTrialUser(true);
      if (userData.id) {
        try {
          const { data: planData } = await supabase
            .from('meal_plans')
            .select('plan_data')
            .eq('user_id', userData.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
          if (planData?.plan_data) {
            setGeneratedPlan(planData.plan_data);
          }
        } catch(e) {}
      }
      setScreen("dashboard");
    }
  };

  const handleOnboardingComplete = () => {
    if (isEditingPreferences) {
      setIsEditingPreferences(false);
      setScreen("dashboard");
    } else {
      setScreen("pricing");
    }
  };

  const handleGeneratingComplete = async (plan) => {
    setIsTrialUser(true);
    if (plan) {
      setGeneratedPlan(plan);
      localStorage.setItem('bk_generated_plan', JSON.stringify(plan));
      localStorage.setItem('bk_plan_date', new Date().toISOString());
      if (!isTrialUser) localStorage.setItem('bk_paid_plan_date', new Date().toISOString());
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (userId) {
          await fetch('/api/save-plan', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId, plan }),
          });
        }
      } catch(e) {}
    }
    localStorage.removeItem('bk_profile');
    setScreen("dashboard");
  };

  const handleSubscribe = (plan) => {
    setSelectedPlan(plan);
    setIsTrialUser(true);
    setScreen("generating");
  };

  return (
    <div style={{ maxWidth:480, margin:"0 auto", minHeight:"100vh", background:C.bg, position:"relative" }}>
      <style>{`* { box-sizing:border-box; margin:0; padding:0; } ::-webkit-scrollbar { width:0; }`}</style>
      {screen === "splash" && (
        <SplashScreen onLogin={() => setScreen("login")} onSignup={() => setScreen("signup")} />
      )}
      {screen === "login" && (
        <AuthScreen mode="login" onAuth={handleAuth} onBack={() => setScreen("splash")} />
      )}
      {screen === "signup" && (
        <AuthScreen mode="signup" onAuth={handleAuth} onBack={() => setScreen("splash")} />
      )}
      {screen === "checkemail" && (
        <CheckEmailScreen email={user?.email || ""} />
      )}
      {screen === "onboarding" && (
        <BarelyKetoOnboarding
          user={user}
          isEditing={isEditingPreferences}
onComplete={async (answers) => {
            const fullProfile = { ...user, ...answers };
            setUser(fullProfile);
            setOnboardingAnswers(fullProfile);
            localStorage.setItem('bk_profile', JSON.stringify(fullProfile));
            localStorage.setItem('bk_weight', fullProfile.currentWeight || '');
            localStorage.setItem('bk_goalweight', fullProfile.goalWeight || '');

            try {
              const { data: { session } } = await supabase.auth.getSession();
              const userId = session?.user?.id || fullProfile.id;
              if (userId) {
                const profileToSave = { ...fullProfile, id: userId };
                const response = await fetch('/api/save-profile', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ userId, profile: profileToSave }),
                });
                if (!response.ok) {
                  const err = await response.json();
                  console.error('save-profile failed:', err);
                }
              } else {
                console.error('save-profile skipped: no userId');
              }
            } catch(e) {
              console.error('save-profile exception:', e);
            }

            localStorage.removeItem('bk_pending_name');
            handleOnboardingComplete();
          }}
        />
      )}
      {screen === "generating" && (
        <GeneratingScreen
          userName={user?.name || "there"}
          profile={onboardingAnswers || user}
          isTrial={true}
          mealRatings={mealRatings}
          onComplete={(plan) => handleGeneratingComplete(plan)}
        />
      )}
      {screen === "dashboard" && (
        <BarelyKetoDashboard
          user={user}
          isTrialUser={isTrialUser}
          generatedPlan={generatedPlan}
          selectedPlan={selectedPlan}
          onUpgrade={() => setScreen("pricing")}
          onEditPreferences={() => { setIsEditingPreferences(true); setScreen("onboarding"); }}
          onRegeneratePlan={() => setScreen("generating")}
          onRate={(mealName, rating) => setMealRatings(prev => ({ ...prev, [mealName]: rating }))}
        />
      )}
      {screen === "faq" && (
        <BarelyKetoFAQ onBack={() => setScreen("splash")} onSignup={() => setScreen("signup")} />
      )}
      {screen === "pricing" && (
        <PricingScreen
          isTrialUser={isTrialUser}
          isNewUser={!isTrialUser}
          onSubscribe={handleSubscribe}
          onBack={isTrialUser ? () => setScreen("dashboard") : null}
          user={user}
        />
      )}
    </div>
  );
}